import { get, post, put, remove } from './client'
import type { BackendMeta } from './types'

export type GetFolderTree = BackendMeta<'/api/workspaces/{workspace_id}/folders/tree', 'get'>

/** Get the tree of all folders in the workspace, returned in the correct hierarchical structure. */
export const getFolderTree = async (workspaceId: string) =>
  get<undefined, GetFolderTree['successResponse'], GetFolderTree['path']>(
    `/api/workspaces/${workspaceId}/folders/tree`,
    undefined,
  )

export type GetFolder = BackendMeta<'/api/workspaces/{workspace_id}/folders/{folder_id}', 'get'>

/** Get a single folder by its ID. */
export const getFolder = async ({
  workspaceId,
  folderId,
}: {
  workspaceId: string
  folderId: string
}) =>
  get<undefined, GetFolder['successResponse'], GetFolder['path']>(
    `/api/workspaces/${workspaceId}/folders/${folderId}`,
    undefined,
  )

export type CreateFolder = BackendMeta<'/api/workspaces/{workspace_id}/folders', 'post'>

/** Create a new folder. */
export const createFolder = async ({
  workspaceId,
  name,
  parentFolderId,
}: {
  workspaceId: string
  name?: string | null
  parentFolderId?: string
}) =>
  post<CreateFolder['body'], CreateFolder['successResponse'], CreateFolder['path']>(
    `/api/workspaces/${workspaceId}/folders`,
    {
      name: name || null,
      parent_folder_id: parentFolderId || null,
    },
  )

export type UpdateFolder = BackendMeta<'/api/workspaces/{workspace_id}/folders/{folder_id}', 'put'>

export type UpdateFolderParams = {
  workspaceId: string
  folderId: string
  name?: UpdateFolder['body']['name']
}

/** Update a folder. */
export const updateFolder = async ({ workspaceId, folderId, name }: UpdateFolderParams) =>
  put<UpdateFolder['body'], UpdateFolder['successResponse'], UpdateFolder['path']>(
    `/api/workspaces/${workspaceId}/folders/${folderId}`,
    {
      name,
    },
  )

export type DeleteFolder = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}',
  'delete'
>

/** Delete a folder. */
export const deleteFolder = async ({
  workspaceId,
  folderId,
}: {
  workspaceId: string
  folderId: string
}) =>
  remove<undefined, DeleteFolder['successResponse'], DeleteFolder['path']>(
    `/api/workspaces/${workspaceId}/folders/${folderId}?recursive=true`,
    undefined,
  )

export type ListFolderMembers = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}/members',
  'get'
>

export type ListFolderMembersParams = {
  workspaceId: ListFolderMembers['pathParams']['workspace_id']
  folderId: ListFolderMembers['pathParams']['folder_id']
}

/** List all the users who have been given an explicit role in the given folder. */
export const listFolderMembers = ({ folderId, workspaceId }: ListFolderMembersParams) =>
  get<
    ListFolderMembers['queryParams'],
    ListFolderMembers['successResponse'],
    ListFolderMembers['path']
  >(`/api/workspaces/${workspaceId}/folders/${folderId}/members`, {})

export type AddFolderMember = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}/members/{user_id}',
  'post'
>

export type AddFolderMemberParams = {
  workspaceId: string
  folderId: string
  userId: string
  role: AddFolderMember['body']['role']
}

/** Add a user to the given folder, giving them explicit access to it with the given role. */
export const addFolderMember = async ({
  workspaceId,
  folderId,
  userId,
  role,
}: AddFolderMemberParams) =>
  post<AddFolderMember['body'], AddFolderMember['successResponse'], AddFolderMember['path']>(
    `/api/workspaces/${workspaceId}/folders/${folderId}/members/${userId}`,
    {
      role,
    },
  )

export type UpdateFolderMember = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}/members/{user_id}',
  'put'
>

export type UpdateFolderMemberParams = {
  workspaceId: string
  folderId: string
  userId: string
  role: UpdateFolderMember['body']['role']
}

/** Update the role of a user in the given folder. */
export const updateFolderMember = async ({
  workspaceId,
  folderId,
  userId,
  role,
}: UpdateFolderMemberParams) =>
  put<
    UpdateFolderMember['body'],
    UpdateFolderMember['successResponse'],
    UpdateFolderMember['path']
  >(`/api/workspaces/${workspaceId}/folders/${folderId}/members/${userId}`, {
    role,
  })

export type DeleteFolderMember = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}/members/{user_id}',
  'delete'
>

export type DeleteFolderMemberParams = {
  workspaceId: string
  folderId: string
  userId: string
}

/** Remove a user from the given folder. */
export const deleteFolderMember = async ({
  workspaceId,
  folderId,
  userId,
}: DeleteFolderMemberParams) =>
  remove<
    DeleteFolderMember['body'],
    DeleteFolderMember['successResponse'],
    DeleteFolderMember['path']
  >(`/api/workspaces/${workspaceId}/folders/${folderId}/members/${userId}`, undefined)

export type UpdateFolderAccess = BackendMeta<
  '/api/workspaces/{workspace_id}/folders/{folder_id}/members/access_type',
  'put'
>

export type UpdateFolderAccessParams = {
  workspaceId: string
  folderId: string
  accessType: UpdateFolderAccess['body']['access_type']
}

/** Update the default access type of the given folder. */
export const updateFolderAccess = async ({
  workspaceId,
  folderId,
  accessType,
}: UpdateFolderAccessParams) =>
  put<
    UpdateFolderAccess['body'],
    UpdateFolderAccess['successResponse'],
    UpdateFolderAccess['path']
  >(`/api/workspaces/${workspaceId}/folders/${folderId}/members/access_type`, {
    access_type: accessType,
  })
